import { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom/client";
import html2canvas from "html2canvas";

/* ══════════════════════════════════════════════════
   FACEBOOK ADS V2 — 15 Creatives (3 per angle)
   V1 Curiosité · V2 Douleur · V3 Benchmark · V4 ROAS · V5 Ultra court
   ══════════════════════════════════════════════════ */

/* ── Shared atoms ── */

function Brand({ light, size = 11 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <svg width={size * 1.4} height={size * 1.4} viewBox="0 0 48 48" fill="none">
        <path d="M18 4L20.5 14L31 16.5L20.5 19L18 29L15.5 19L5 16.5L15.5 14Z" fill={light ? "#fff" : "#4B6AE8"}/>
        <path d="M34 6L35.2 10L39 11.2L35.2 12.4L34 16L32.8 12.4L29 11.2L32.8 10Z" fill={light ? "rgba(255,255,255,0.7)" : "#4B6AE8"} opacity="0.7"/>
      </svg>
      <span style={{ fontSize: size, fontWeight: 700, color: light ? "#fff" : "#0f172a", fontFamily: "'Inter',sans-serif" }}>
        Magic<span style={{ fontWeight: 400 }}>Feed</span>
        <span style={{ color: light ? "#c7d2fe" : "#4B6AE8", fontSize: size * 0.78, fontWeight: 700, marginLeft: 1 }}>Pro</span>
      </span>
    </div>
  );
}

/* Circular score gauge */
function ScoreGauge({ score, size = 100, strokeWidth = 5 }) {
  const r = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * r;
  const color = score < 40 ? "#EF4444" : score < 70 ? "#F59E0B" : "#10B981";
  return (
    <div style={{ position: "relative", width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <svg width={size} height={size} style={{ position: "absolute" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth={strokeWidth}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={`${(score / 100) * circ} ${circ}`} strokeLinecap="round"
          style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%", transition: "stroke-dasharray 0.08s linear, stroke 0.3s ease" }}/>
      </svg>
      <div style={{ textAlign: "center", zIndex: 1 }}>
        <div style={{ fontSize: size * 0.3, fontWeight: 900, color, lineHeight: 1, transition: "color 0.3s ease" }}>{score}</div>
        <div style={{ fontSize: size * 0.09, color: "#94a3b8", fontWeight: 600 }}>/100</div>
      </div>
    </div>
  );
}

/* URL input mockup */
function UrlInput({ typing, url, active }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", background: "#fff", border: active ? "2px solid #4B6AE8" : "1px solid #e5e7eb",
      borderRadius: 10, padding: "8px 12px", boxShadow: active ? "0 0 0 3px rgba(75,106,232,0.1)" : "0 1px 3px rgba(0,0,0,0.04)",
      transition: "all 0.3s ease",
    }}>
      <span style={{ fontSize: 10, color: "#94a3b8", marginRight: 4 }}>🔗</span>
      <span style={{ fontSize: 11, color: url ? "#0f172a" : "#94a3b8", fontWeight: url ? 600 : 400, flex: 1 }}>
        {url || "yourstore.myshopify.com"}
      </span>
      {typing && <div style={{ width: 1, height: 14, background: "#4B6AE8", animation: "pulseMF 0.5s infinite" }}/>}
    </div>
  );
}

/* Simple check row */
function CheckRow({ label, status, detail, active }) {
  const c = status === "pass" ? "#10B981" : status === "fail" ? "#EF4444" : "#94a3b8";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, padding: "5px 10px",
      background: active ? (status === "pass" ? "#f0fdf4" : status === "fail" ? "#fef2f2" : "#fafafa") : "#fafafa",
      borderRadius: 8, border: `1px solid ${active ? (status === "pass" ? "#bbf7d0" : status === "fail" ? "#fecaca" : "#e5e7eb") : "#f1f1f1"}`,
      opacity: active ? 1 : 0.25, transition: "all 0.5s ease",
    }}>
      <div style={{ width: 16, height: 16, borderRadius: "50%", background: active ? c : "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.3s ease" }}>
        <span style={{ color: "#fff", fontSize: 8, fontWeight: 800 }}>{active ? (status === "pass" ? "✓" : status === "fail" ? "✗" : "—") : ""}</span>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: "#0f172a" }}>{label}</div>
        {active && detail && <div style={{ fontSize: 8, color: c, fontWeight: 600, marginTop: 1 }}>{detail}</div>}
      </div>
    </div>
  );
}

/* Impact stat card */
function StatCard({ icon, value, label, active, color = "#4B6AE8" }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 10, padding: "10px 12px", border: "1px solid #e5e7eb",
      textAlign: "center", opacity: active ? 1 : 0, transform: active ? "translateY(0)" : "translateY(10px)",
      transition: "all 0.5s ease", boxShadow: active ? "0 2px 8px rgba(0,0,0,0.04)" : "none",
    }}>
      <div style={{ fontSize: 16, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 18, fontWeight: 900, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 8, color: "#94a3b8", fontWeight: 600, marginTop: 3 }}>{label}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   V1 — CURIOSITÉ
   "What's your Shopify feed score?"
   ══════════════════════════════════════════════════ */

/* V1-A: Score Reveal — Gauge counting up with suspense */
function V1A() {
  const [step, setStep] = useState(0);
  const [score, setScore] = useState(0);
  const [typedUrl, setTypedUrl] = useState("");
  const URL_TEXT = "mystore.myshopify.com";

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setScore(0); setTypedUrl(""); await w(800);
        // Type URL
        setStep(1);
        for (let i = 1; i <= URL_TEXT.length; i++) { if (!running) return; setTypedUrl(URL_TEXT.slice(0, i)); await w(40); }
        await w(600);
        // Scan
        setStep(2); await w(1000);
        // Score counting
        setStep(3);
        for (let i = 0; i <= 32; i++) { if (!running) return; setScore(i); await w(50); }
        await w(600);
        // Bad score reaction
        setStep(4); await w(2000);
        // Show checks
        setStep(5); await w(1000);
        setStep(6); await w(1000);
        setStep(7); await w(1000);
        // CTA
        setStep(8); await w(4000);
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #fafbff 0%, #eef2ff 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 600, color: "#94a3b8" }}>Free · 30 seconds</div>
      </div>

      <div style={{ flex: 1, padding: "0 16px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {/* Question */}
        {step < 3 && (
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", lineHeight: 1.3 }}>What's your Shopify<br/>feed score?</div>
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 6 }}>Most stores score under 40/100</div>
          </div>
        )}

        {/* URL input */}
        {step >= 1 && step < 3 && (
          <div style={{ width: "100%", marginBottom: 12 }}>
            <UrlInput typing={step === 1 && typedUrl.length < URL_TEXT.length} url={typedUrl} active={step >= 1}/>
          </div>
        )}

        {/* Scanning */}
        {step === 2 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, animation: "popIn 0.3s ease" }}>
            <div style={{ width: 12, height: 12, border: "2px solid #4B6AE8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
            <span style={{ fontSize: 10, color: "#4B6AE8", fontWeight: 600 }}>Scanning your feed...</span>
          </div>
        )}

        {/* Score gauge */}
        {step >= 3 && (
          <>
            <ScoreGauge score={score} size={120} strokeWidth={6}/>
            {step >= 4 && (
              <div style={{ marginTop: 8, animation: "popIn 0.4s ease" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#EF4444", textAlign: "center" }}>Your feed needs attention</div>
                <div style={{ fontSize: 9, color: "#94a3b8", textAlign: "center", marginTop: 2 }}>3 issues found in your product data</div>
              </div>
            )}
          </>
        )}

        {/* Check rows */}
        {step >= 5 && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 4, marginTop: 12 }}>
            <CheckRow label="Title Quality" active={step >= 5} status="fail" detail='12 chars — "Laptop bag"'/>
            <CheckRow label="Description SEO" active={step >= 6} status="fail" detail="120 chars — needs 500+"/>
            <CheckRow label="Product Highlights" active={step >= 7} status="fail" detail="0/5 — Google requires 5"/>
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #4B6AE8, #6B8BF5)", color: "#fff", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, boxShadow: "0 4px 14px rgba(75,106,232,0.35)" }}>
          {step < 3 ? "Enter Your URL →" : "Get My Full Report — Free"}
        </div>
      </div>
    </div>
  );
}

/* V1-B: Question Stack — Questions appearing one by one */
function V1B() {
  const [step, setStep] = useState(0);
  const [score, setScore] = useState(0);

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setScore(0); await w(600);
        setStep(1); await w(2000); // question 1
        setStep(2); await w(2000); // question 2
        setStep(3); await w(2000); // question 3
        setStep(4); await w(1200); // "let's find out"
        setStep(5); // counting
        for (let i = 0; i <= 32; i++) { if (!running) return; setScore(i); await w(40); }
        await w(600);
        setStep(6); await w(1000); // checks
        setStep(7); await w(1000);
        setStep(8); await w(1000);
        setStep(9); await w(4000); // CTA
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  const questions = [
    { s: 1, text: "Do your products show up on Google?" },
    { s: 2, text: "Are your titles optimized for clicks?" },
    { s: 3, text: "Is your feed score above 70?" },
  ];

  return (
    <div style={{ width: "100%", height: "100%", background: "#0f172a", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand light/>
        <div style={{ fontSize: 8, fontWeight: 600, color: "#64748b" }}>30 sec · Free</div>
      </div>

      <div style={{ flex: 1, padding: "0 20px", display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
        {/* Questions */}
        {step < 4 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {questions.map((q, i) => (
              <div key={i} style={{
                background: step >= q.s ? "rgba(255,255,255,0.06)" : "transparent",
                borderRadius: 12, padding: step >= q.s ? "12px 16px" : "0 16px",
                border: `1px solid ${step >= q.s ? "rgba(255,255,255,0.1)" : "transparent"}`,
                opacity: step >= q.s ? 1 : 0, maxHeight: step >= q.s ? 60 : 0,
                transition: "all 0.6s ease", overflow: "hidden",
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4 }}>{q.text}</div>
                {step >= q.s && (
                  <div style={{ fontSize: 9, color: "#f87171", fontWeight: 700, marginTop: 4 }}>87% of Shopify stores can't answer "yes"</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Score reveal */}
        {step >= 4 && (
          <div style={{ textAlign: "center" }}>
            {step === 4 && (
              <div style={{ animation: "popIn 0.5s ease" }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", marginBottom: 8 }}>Let's find out.</div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 12, height: 12, border: "2px solid #4B6AE8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
                  <span style={{ fontSize: 10, color: "#818cf8", fontWeight: 600 }}>Scanning...</span>
                </div>
              </div>
            )}
            {step >= 5 && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <ScoreGauge score={score} size={110} strokeWidth={5}/>
                {step >= 6 && (
                  <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 4, marginTop: 14 }}>
                    {[
                      { s: 6, label: "📝 Titles", detail: "Too short" },
                      { s: 7, label: "📄 Descriptions", detail: "Missing keywords" },
                      { s: 8, label: "⭐ Highlights", detail: "0/5 filled" },
                    ].map((c, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 8, padding: "5px 10px",
                        background: step >= c.s ? "rgba(239,68,68,0.08)" : "transparent",
                        borderRadius: 6, opacity: step >= c.s ? 1 : 0, transition: "all 0.5s ease",
                      }}>
                        <span style={{ fontSize: 10 }}>{c.label}</span>
                        <span style={{ marginLeft: "auto", fontSize: 8, color: "#f87171", fontWeight: 700 }}>{c.detail}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "#fff", color: "#0f172a", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, boxShadow: "0 4px 14px rgba(0,0,0,0.3)" }}>
          What's My Score?
        </div>
      </div>
    </div>
  );
}

/* V1-C: Scratch Card — score hidden then revealed */
function V1C() {
  const [step, setStep] = useState(0);
  const [revealPct, setRevealPct] = useState(0);

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setRevealPct(0); await w(800);
        setStep(1); await w(2000); // hook text
        setStep(2); await w(1500); // card appears
        setStep(3); // scratch reveal
        for (let i = 0; i <= 100; i += 2) { if (!running) return; setRevealPct(i); await w(30); }
        await w(600);
        setStep(4); await w(1500); // bad score
        setStep(5); await w(1000); // check 1
        setStep(6); await w(1000); // check 2
        setStep(7); await w(1000); // check 3
        setStep(8); await w(4000); // CTA
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #fbbf24 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 700, color: "#92400e" }}>Free Audit</div>
      </div>

      <div style={{ flex: 1, padding: "0 20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {step >= 1 && step < 4 && (
          <div style={{ textAlign: "center", marginBottom: 16, animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", lineHeight: 1.3 }}>What's hiding behind<br/>your feed score?</div>
          </div>
        )}

        {/* Scratch card */}
        {step >= 2 && step < 4 && (
          <div style={{ width: 160, height: 100, borderRadius: 14, position: "relative", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}>
            {/* Score underneath */}
            <div style={{ position: "absolute", inset: 0, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 36, fontWeight: 900, color: "#EF4444" }}>32</div>
                <div style={{ fontSize: 10, color: "#94a3b8" }}>/100</div>
              </div>
            </div>
            {/* Scratch overlay */}
            <div style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(135deg, #c0c0c0, #e0e0e0, #c0c0c0)",
              clipPath: `inset(0 ${100 - revealPct}% 0 0)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "clip-path 0.05s linear",
            }}>
              <span style={{ fontSize: 11, color: "#666", fontWeight: 700 }}>🔍 Scratch to reveal</span>
            </div>
          </div>
        )}

        {/* Score revealed */}
        {step >= 4 && (
          <div style={{ textAlign: "center", animation: "popIn 0.4s ease" }}>
            <ScoreGauge score={32} size={100} strokeWidth={5}/>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#EF4444", marginTop: 6 }}>Ouch. Your feed needs help.</div>
          </div>
        )}

        {/* Checks */}
        {step >= 5 && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 4, marginTop: 12 }}>
            <CheckRow label="Title Quality" active={step >= 5} status="fail" detail="12 chars avg"/>
            <CheckRow label="Description" active={step >= 6} status="fail" detail="120 chars avg"/>
            <CheckRow label="Highlights" active={step >= 7} status="fail" detail="0/5 filled"/>
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "#0f172a", color: "#fbbf24", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, boxShadow: "0 4px 14px rgba(0,0,0,0.2)" }}>
          Reveal My Score — Free
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   V2 — DOULEUR
   "Your Shopify feed is costing you sales"
   ══════════════════════════════════════════════════ */

/* V2-A: Bleeding Sales — Red alert, mistakes revealed */
function V2A() {
  const [step, setStep] = useState(0);
  const [salesLost, setSalesLost] = useState(0);

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setSalesLost(0); await w(600);
        setStep(1); await w(1500); // alert
        setStep(2); // counter
        for (let i = 0; i <= 87; i++) { if (!running) return; setSalesLost(i); await w(30); }
        await w(1000);
        setStep(3); await w(1200); // mistake 1
        setStep(4); await w(1200); // mistake 2
        setStep(5); await w(1200); // mistake 3
        setStep(6); await w(1500); // "sound familiar?"
        setStep(7); await w(4000); // CTA
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #fef2f2 0%, #fee2e2 50%, #fecaca 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 700, color: "#dc2626", animation: step >= 1 ? "pulseMF 1.5s infinite" : "none" }}>🚨 ALERT</div>
      </div>

      <div style={{ flex: 1, padding: "0 20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {/* Headline */}
        {step >= 1 && (
          <div style={{ textAlign: "center", marginBottom: 16, animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", lineHeight: 1.3 }}>Your Shopify feed is<br/>costing you sales</div>
          </div>
        )}

        {/* Stat */}
        {step >= 2 && (
          <div style={{ textAlign: "center", marginBottom: 16, animation: "popIn 0.4s ease" }}>
            <div style={{ fontSize: 48, fontWeight: 900, color: "#EF4444", lineHeight: 1 }}>{salesLost}%</div>
            <div style={{ fontSize: 11, color: "#991b1b", fontWeight: 600, marginTop: 4 }}>of stores have the same 3 mistakes</div>
          </div>
        )}

        {/* 3 mistakes */}
        {step >= 3 && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              { s: 3, num: "1", title: "Bad product titles", detail: "Generic names that Google ignores", icon: "📝" },
              { s: 4, num: "2", title: "Weak descriptions", detail: "Too short for SEO ranking", icon: "📄" },
              { s: 5, num: "3", title: "Zero highlights", detail: "Google requires them — you have none", icon: "⭐" },
            ].map((m, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 10, background: "#fff",
                borderRadius: 10, padding: "8px 12px", border: "1px solid #fecaca",
                opacity: step >= m.s ? 1 : 0, transform: step >= m.s ? "translateX(0)" : "translateX(-20px)",
                transition: "all 0.5s ease",
              }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ fontSize: 14 }}>{m.icon}</span>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#0f172a" }}>#{m.num} {m.title}</div>
                  <div style={{ fontSize: 8, color: "#dc2626", fontWeight: 600, marginTop: 1 }}>{m.detail}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {step >= 6 && (
          <div style={{ marginTop: 14, textAlign: "center", animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>Sound familiar?</div>
            <div style={{ fontSize: 9, color: "#64748b", marginTop: 2 }}>Find out in 30 seconds. Free.</div>
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "#EF4444", color: "#fff", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, boxShadow: "0 4px 14px rgba(239,68,68,0.35)" }}>
          Check My Feed — Free
        </div>
      </div>
    </div>
  );
}

/* V2-B: Pulse Monitor — heartbeat flatline style */
function V2B() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); await w(600);
        setStep(1); await w(1500); // flatline
        setStep(2); await w(1500); // "your feed is dying"
        setStep(3); await w(1200); // symptom 1
        setStep(4); await w(1200); // symptom 2
        setStep(5); await w(1200); // symptom 3
        setStep(6); await w(1500); // diagnosis
        setStep(7); await w(1500); // "we can fix this"
        setStep(8); await w(4000); // CTA
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "#0f172a", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>
      {/* Pulse line */}
      {step >= 1 && step < 7 && (
        <div style={{ position: "absolute", top: "25%", left: 0, right: 0, height: 2, background: "#EF4444", opacity: 0.3 }}/>
      )}

      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 1 }}>
        <Brand light/>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: step >= 7 ? "#10B981" : "#EF4444", animation: "pulseMF 1s infinite" }}/>
          <span style={{ fontSize: 8, color: step >= 7 ? "#10B981" : "#EF4444", fontWeight: 700 }}>{step >= 7 ? "HEALTHY" : "CRITICAL"}</span>
        </div>
      </div>

      <div style={{ flex: 1, padding: "0 20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden", zIndex: 1 }}>
        {step >= 2 && step < 7 && (
          <div style={{ textAlign: "center", marginBottom: 20, animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>Your feed is<br/><span style={{ color: "#EF4444" }}>losing you sales</span></div>
          </div>
        )}

        {step >= 7 && (
          <div style={{ textAlign: "center", marginBottom: 20, animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>We can <span style={{ color: "#10B981" }}>fix this</span></div>
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 6 }}>AI audit in 30 seconds. Free.</div>
          </div>
        )}

        {/* Symptoms */}
        {step >= 3 && step < 7 && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              { s: 3, symptom: "Titles too short", impact: "→ 73% lower click rate" },
              { s: 4, symptom: "Descriptions missing", impact: "→ Google can't index you" },
              { s: 5, symptom: "No product highlights", impact: "→ Products get rejected" },
            ].map((sym, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "8px 12px", background: "rgba(239,68,68,0.08)", borderRadius: 8,
                borderLeft: "3px solid #EF4444",
                opacity: step >= sym.s ? 1 : 0, transition: "opacity 0.5s ease",
              }}>
                <span style={{ fontSize: 10, color: "#fca5a5", fontWeight: 600 }}>{sym.symptom}</span>
                <span style={{ fontSize: 8, color: "#EF4444", fontWeight: 700 }}>{sym.impact}</span>
              </div>
            ))}
          </div>
        )}

        {step >= 6 && step < 7 && (
          <div style={{ marginTop: 14, textAlign: "center", animation: "popIn 0.4s ease" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(239,68,68,0.15)", borderRadius: 8, padding: "6px 14px" }}>
              <span style={{ fontSize: 10 }}>⚠️</span>
              <span style={{ fontSize: 10, color: "#f87171", fontWeight: 700 }}>Feed Score: 32/100 — Critical</span>
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center", zIndex: 1 }}>
        <div style={{ display: "inline-block", background: step >= 7 ? "linear-gradient(135deg, #10B981, #34d399)" : "#fff", color: step >= 7 ? "#fff" : "#EF4444", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, transition: "all 0.5s ease" }}>
          {step >= 7 ? "Fix My Feed — Free" : "Diagnose My Feed"}
        </div>
      </div>
    </div>
  );
}

/* V2-C: Warning Tape — construction/danger zone */
function V2C() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); await w(600);
        setStep(1); await w(1800); // danger zone
        setStep(2); await w(1500); // mistake 1
        setStep(3); await w(1500); // mistake 2
        setStep(4); await w(1500); // mistake 3
        setStep(5); await w(1500); // "87% of stores"
        setStep(6); await w(1500); // fix available
        setStep(7); await w(4000); // CTA
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "#fff", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Warning stripe header */}
      <div style={{ height: 6, background: "repeating-linear-gradient(45deg, #f59e0b, #f59e0b 10px, #0f172a 10px, #0f172a 20px)" }}/>

      <div style={{ padding: "12px 16px 6px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 700, color: "#d97706", textTransform: "uppercase", letterSpacing: 1 }}>⚠️ Feed Warning</div>
      </div>

      <div style={{ flex: 1, padding: "0 20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {step >= 1 && (
          <div style={{ textAlign: "center", marginBottom: 16, animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", lineHeight: 1.3 }}>3 fixable mistakes are<br/><span style={{ color: "#EF4444" }}>killing your visibility</span></div>
          </div>
        )}

        {step >= 2 && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              { s: 2, icon: "❌", title: "Bad titles", sub: "Generic names = invisible on Google" },
              { s: 3, icon: "❌", title: "Weak descriptions", sub: "120 chars? Google needs 500+" },
              { s: 4, icon: "❌", title: "Zero highlights", sub: "Google requires 5 — you have 0" },
            ].map((m, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                background: step >= m.s ? "#fef2f2" : "#fafafa", borderRadius: 10,
                border: `1px solid ${step >= m.s ? "#fecaca" : "#f1f1f1"}`,
                opacity: step >= m.s ? 1 : 0, transition: "all 0.5s ease",
              }}>
                <span style={{ fontSize: 16 }}>{m.icon}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}>{m.title}</div>
                  <div style={{ fontSize: 9, color: "#dc2626", fontWeight: 500, marginTop: 1 }}>{m.sub}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {step >= 5 && step < 6 && (
          <div style={{ marginTop: 14, animation: "popIn 0.5s ease", textAlign: "center" }}>
            <span style={{ fontSize: 28, fontWeight: 900, color: "#EF4444" }}>87%</span>
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>of Shopify stores have these issues</div>
          </div>
        )}

        {step >= 6 && (
          <div style={{ marginTop: 14, animation: "popIn 0.5s ease", textAlign: "center" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#ecfdf5", border: "1px solid #6ee7b7", borderRadius: 8, padding: "8px 16px" }}>
              <span style={{ fontSize: 10 }}>✦</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#059669" }}>All 3 are fixable — in 30 seconds</span>
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #EF4444, #f87171)", color: "#fff", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, boxShadow: "0 4px 14px rgba(239,68,68,0.35)" }}>
          Fix My Feed — Free
        </div>
      </div>

      <div style={{ height: 6, background: "repeating-linear-gradient(45deg, #f59e0b, #f59e0b 10px, #0f172a 10px, #0f172a 20px)" }}/>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   V3 — BENCHMARK
   "Industry average: 74/100. Where do you stand?"
   ══════════════════════════════════════════════════ */

/* V3-A: Bell Curve — You vs the average */
function V3A() {
  const [step, setStep] = useState(0);
  const [avg, setAvg] = useState(0);
  const [yours, setYours] = useState(0);
  const [audited, setAudited] = useState(0);

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setAvg(0); setYours(0); setAudited(0); await w(600);
        setStep(1); // audited counter
        for (let i = 0; i <= 800; i += 16) { if (!running) return; setAudited(Math.min(i, 800)); await w(20); }
        await w(800);
        setStep(2); // avg counting
        for (let i = 0; i <= 74; i++) { if (!running) return; setAvg(i); await w(30); }
        await w(800);
        setStep(3); await w(1500); // "most under 40"
        setStep(4); // your score
        for (let i = 0; i <= 32; i++) { if (!running) return; setYours(i); await w(50); }
        await w(600);
        setStep(5); await w(1500); // checks
        setStep(6); await w(1000);
        setStep(7); await w(1000);
        setStep(8); await w(4000); // CTA
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 600, color: "#0284c7" }}>📊 Benchmark</div>
      </div>

      <div style={{ flex: 1, padding: "0 20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {step >= 1 && (
          <div style={{ textAlign: "center", marginBottom: 12, animation: "popIn 0.4s ease" }}>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>We audited</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: "#0284c7", lineHeight: 1 }}>{audited}+</div>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>Shopify stores</div>
          </div>
        )}

        {/* Side by side scores */}
        {step >= 2 && (
          <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 12 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 8, fontWeight: 600, color: "#64748b", marginBottom: 4 }}>Industry Avg</div>
              <ScoreGauge score={avg} size={80} strokeWidth={4}/>
            </div>
            {step >= 4 && (
              <>
                <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8" }}>vs</div>
                <div style={{ textAlign: "center", animation: "popIn 0.4s ease" }}>
                  <div style={{ fontSize: 8, fontWeight: 600, color: "#64748b", marginBottom: 4 }}>Your Store?</div>
                  <ScoreGauge score={yours} size={80} strokeWidth={4}/>
                </div>
              </>
            )}
          </div>
        )}

        {step >= 3 && step < 4 && (
          <div style={{ textAlign: "center", animation: "popIn 0.4s ease" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Most stores score <span style={{ color: "#EF4444" }}>under 40</span></div>
            <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>without even knowing it</div>
          </div>
        )}

        {step >= 5 && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 4 }}>
            <CheckRow label="Title Quality" active={step >= 5} status="fail" detail="Below average"/>
            <CheckRow label="Description SEO" active={step >= 6} status="fail" detail="Below average"/>
            <CheckRow label="Highlights" active={step >= 7} status="fail" detail="Missing entirely"/>
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #0284c7, #38bdf8)", color: "#fff", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, boxShadow: "0 4px 14px rgba(2,132,199,0.35)" }}>
          Where Do I Stand? — Free
        </div>
      </div>
    </div>
  );
}

/* V3-B: Leaderboard — ranked list style */
function V3B() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); await w(600);
        setStep(1); await w(1200); // row 1
        setStep(2); await w(1000); // row 2
        setStep(3); await w(1000); // row 3
        setStep(4); await w(1000); // row 4
        setStep(5); await w(1500); // your store appears
        setStep(6); await w(2000); // highlight
        setStep(7); await w(1500); // "fix it"
        setStep(8); await w(4000); // CTA
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  const rows = [
    { s: 1, rank: "#1", name: "top-gadgets.com", score: 96, color: "#10B981" },
    { s: 2, rank: "#2", name: "elite-fashion.com", score: 91, color: "#10B981" },
    { s: 3, rank: "#3", name: "best-home.com", score: 84, color: "#F59E0B" },
    { s: 4, rank: "Avg", name: "Industry average", score: 74, color: "#0284c7" },
    { s: 5, rank: "?", name: "Your store", score: 32, color: "#EF4444", isYou: true },
  ];

  return (
    <div style={{ width: "100%", height: "100%", background: "#fff", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 600, color: "#94a3b8" }}>Feed Score Leaderboard</div>
      </div>

      <div style={{ padding: "0 16px 4px" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>How does your store compare?</div>
        <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>Based on 800+ Shopify stores audited</div>
      </div>

      <div style={{ flex: 1, padding: "8px 16px", display: "flex", flexDirection: "column", gap: 5, overflow: "hidden" }}>
        {rows.map((r, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
            background: r.isYou ? "#fef2f2" : "#fafafa",
            borderRadius: 10, border: `1px solid ${r.isYou ? "#fecaca" : "#f1f1f1"}`,
            opacity: step >= r.s ? 1 : 0, transform: step >= r.s ? "translateY(0)" : "translateY(10px)",
            transition: "all 0.5s ease",
            boxShadow: r.isYou && step >= 6 ? "0 0 0 2px #EF4444" : "none",
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: 8, flexShrink: 0,
              background: r.isYou ? "#EF4444" : "#f1f5f9",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span style={{ fontSize: 9, fontWeight: 800, color: r.isYou ? "#fff" : "#64748b" }}>{r.rank}</span>
            </div>
            <span style={{ fontSize: 10, fontWeight: 600, color: "#0f172a", flex: 1 }}>{r.name}</span>
            {/* Score bar */}
            <div style={{ width: 60, height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ width: `${r.score}%`, height: "100%", background: r.color, borderRadius: 3, transition: "width 0.8s ease" }}/>
            </div>
            <span style={{ fontSize: 11, fontWeight: 800, color: r.color, minWidth: 24, textAlign: "right" }}>{r.score}</span>
          </div>
        ))}

        {step >= 6 && (
          <div style={{ textAlign: "center", marginTop: 8, animation: "popIn 0.4s ease" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#EF4444" }}>You're below average.</span>
            <span style={{ fontSize: 10, color: "#64748b" }}> But it's fixable.</span>
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #4B6AE8, #6B8BF5)", color: "#fff", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, boxShadow: "0 4px 14px rgba(75,106,232,0.35)" }}>
          Check My Rank — Free
        </div>
      </div>
    </div>
  );
}

/* V3-C: Distribution Chart — histogram style */
function V3C() {
  const [step, setStep] = useState(0);
  const [bars, setBars] = useState([0,0,0,0,0]);

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setBars([0,0,0,0,0]); await w(800);
        setStep(1); await w(800); // title
        // Animate bars
        const targets = [35, 28, 18, 12, 7]; // percentages
        for (let frame = 0; frame <= 20; frame++) {
          if (!running) return;
          setBars(targets.map(t => Math.round(t * frame / 20)));
          await w(50);
        }
        setStep(2); await w(1500); // highlight "you"
        setStep(3); await w(2000); // avg line
        setStep(4); await w(1500); // "where are you?"
        setStep(5); await w(1500); // checks
        setStep(6); await w(1000);
        setStep(7); await w(4000); // CTA
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  const barLabels = ["0-20", "21-40", "41-60", "61-80", "81-100"];
  const barColors = ["#EF4444", "#F59E0B", "#F59E0B", "#10B981", "#10B981"];

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 600, color: "#64748b" }}>800+ stores audited</div>
      </div>

      <div style={{ flex: 1, padding: "0 20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {step >= 1 && (
          <div style={{ textAlign: "center", marginBottom: 16, animation: "popIn 0.4s ease" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>Feed Score Distribution</div>
            <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>Where does your Shopify store fall?</div>
          </div>
        )}

        {/* Histogram */}
        <div style={{ width: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 6, height: 120, marginBottom: 12 }}>
          {bars.map((pct, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
              <span style={{ fontSize: 8, fontWeight: 700, color: barColors[i], marginBottom: 2 }}>{pct}%</span>
              <div style={{
                width: "100%", height: `${pct * 2.5}px`, minHeight: 4,
                background: step >= 2 && i <= 1 ? barColors[i] : `${barColors[i]}60`,
                borderRadius: "4px 4px 0 0", transition: "all 0.5s ease",
                boxShadow: step >= 2 && i <= 1 ? `0 0 0 2px ${barColors[i]}` : "none",
              }}/>
              <span style={{ fontSize: 7, color: "#64748b", marginTop: 4, fontWeight: 600 }}>{barLabels[i]}</span>
            </div>
          ))}
        </div>

        {step >= 2 && (
          <div style={{ animation: "popIn 0.4s ease", textAlign: "center" }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#EF4444" }}>63% of stores</span>
            <span style={{ fontSize: 10, color: "#64748b" }}> score under 40</span>
          </div>
        )}

        {step >= 4 && (
          <div style={{ marginTop: 10, textAlign: "center", animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>Where are you?</div>
            <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>30 seconds. No account needed.</div>
          </div>
        )}

        {step >= 5 && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 3, marginTop: 10 }}>
            <CheckRow label="Title" active={step >= 5} status="fail" detail="Most common issue"/>
            <CheckRow label="Description" active={step >= 6} status="fail" detail="Too short for SEO"/>
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #4B6AE8, #6B8BF5)", color: "#fff", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, boxShadow: "0 4px 14px rgba(75,106,232,0.35)" }}>
          Find My Position — Free
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   V4 — ROAS
   "+38% revenue. Same ad budget."
   ══════════════════════════════════════════════════ */

/* V4-A: Revenue Counter — big number cascade */
function V4A() {
  const [step, setStep] = useState(0);
  const [impressions, setImpressions] = useState(0);
  const [clicks, setClicks] = useState(0);
  const [revenue, setRevenue] = useState(0);

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setImpressions(0); setClicks(0); setRevenue(0); await w(800);
        setStep(1); await w(1200); // headline
        setStep(2); // impressions
        for (let i = 0; i <= 67; i++) { if (!running) return; setImpressions(i); await w(30); }
        await w(600);
        setStep(3); // clicks
        for (let i = 0; i <= 43; i++) { if (!running) return; setClicks(i); await w(35); }
        await w(600);
        setStep(4); // revenue
        for (let i = 0; i <= 38; i++) { if (!running) return; setRevenue(i); await w(40); }
        await w(600);
        setStep(5); await w(1500); // "same ad budget"
        setStep(6); await w(1500); // "how?"
        setStep(7); await w(4000); // CTA
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #ecfdf5 0%, #d1fae5 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 600, color: "#059669" }}>💰 Revenue Impact</div>
      </div>

      <div style={{ flex: 1, padding: "0 20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {step >= 1 && (
          <div style={{ textAlign: "center", marginBottom: 20, animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", lineHeight: 1.3 }}>Same ad budget.<br/><span style={{ color: "#10B981" }}>More revenue.</span></div>
          </div>
        )}

        {/* Stats */}
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            { s: 2, icon: "👁️", value: `+${impressions}%`, label: "Impressions", color: "#0284c7", current: impressions },
            { s: 3, icon: "👆", value: `+${clicks}%`, label: "Clicks", color: "#7c3aed", current: clicks },
            { s: 4, icon: "💰", value: `+${revenue}%`, label: "Revenue", color: "#10B981", current: revenue },
          ].map((stat, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
              background: "#fff", borderRadius: 12, border: "1px solid #d1fae5",
              opacity: step >= stat.s ? 1 : 0, transform: step >= stat.s ? "translateX(0)" : "translateX(-20px)",
              transition: "all 0.5s ease",
            }}>
              <span style={{ fontSize: 20 }}>{stat.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, color: "#64748b", fontWeight: 600 }}>{stat.label}</div>
                <div style={{ width: "100%", height: 4, background: "#f1f5f9", borderRadius: 2, marginTop: 3, overflow: "hidden" }}>
                  <div style={{ width: `${stat.current}%`, height: "100%", background: stat.color, borderRadius: 2, transition: "width 0.1s linear" }}/>
                </div>
              </div>
              <span style={{ fontSize: 18, fontWeight: 900, color: stat.color }}>{stat.value}</span>
            </div>
          ))}
        </div>

        {step >= 5 && (
          <div style={{ marginTop: 14, textAlign: "center", animation: "popIn 0.5s ease" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", borderRadius: 8, padding: "6px 14px", border: "1px solid #d1fae5" }}>
              <span style={{ fontSize: 10 }}>🎯</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#059669" }}>Without spending more on ads</span>
            </div>
          </div>
        )}

        {step >= 6 && (
          <div style={{ marginTop: 10, textAlign: "center", animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 11, color: "#374151", fontWeight: 600 }}>How? By fixing your product feed.</div>
            <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>Start with a free audit.</div>
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #059669, #10B981)", color: "#fff", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, boxShadow: "0 4px 14px rgba(5,150,105,0.35)" }}>
          Unlock My Revenue — Free
        </div>
      </div>
    </div>
  );
}

/* V4-B: Before/After Dashboard — dark split */
function V4B() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); await w(600);
        setStep(1); await w(1500); // before
        setStep(2); await w(1500); // before stats
        setStep(3); await w(2000); // transition
        setStep(4); await w(1500); // after stats
        setStep(5); await w(1500); // "same budget"
        setStep(6); await w(4000); // CTA
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #0f172a 0%, #1e293b 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand light/>
        <div style={{ fontSize: 8, fontWeight: 600, color: "#64748b" }}>ROAS Impact</div>
      </div>

      <div style={{ flex: 1, padding: "0 16px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 10, overflow: "hidden" }}>
        {/* Before */}
        {step >= 1 && step < 3 && (
          <div style={{ animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#EF4444", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Before Feed Optimization</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              <StatCard icon="👁️" value="2.1K" label="Impressions/day" active={step >= 2} color="#94a3b8"/>
              <StatCard icon="👆" value="84" label="Clicks/day" active={step >= 2} color="#94a3b8"/>
              <StatCard icon="💰" value="$127" label="Revenue/day" active={step >= 2} color="#94a3b8"/>
            </div>
          </div>
        )}

        {/* Transition */}
        {step >= 3 && (
          <div style={{ textAlign: "center", animation: "popIn 0.4s ease" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "linear-gradient(135deg, rgba(75,106,232,0.2), rgba(129,140,248,0.2))", borderRadius: 20, padding: "6px 16px" }}>
              <span style={{ fontSize: 10 }}>✦</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#818cf8" }}>After AI Feed Optimization</span>
            </div>
          </div>
        )}

        {/* After */}
        {step >= 3 && (
          <div style={{ animation: "popIn 0.5s ease" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              <StatCard icon="👁️" value="3.5K" label="+67%" active={step >= 4} color="#10B981"/>
              <StatCard icon="👆" value="120" label="+43%" active={step >= 4} color="#10B981"/>
              <StatCard icon="💰" value="$175" label="+38%" active={step >= 4} color="#10B981"/>
            </div>
          </div>
        )}

        {step >= 5 && (
          <div style={{ textAlign: "center", marginTop: 8, animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#10B981" }}>+$48/day</div>
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>= <span style={{ color: "#10B981", fontWeight: 700 }}>$1,440/month</span> more revenue</div>
            <div style={{ fontSize: 9, color: "#64748b", marginTop: 4 }}>Same ad budget. Better feed.</div>
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "#fff", color: "#0f172a", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, boxShadow: "0 4px 14px rgba(0,0,0,0.3)" }}>
          Start Free Audit →
        </div>
      </div>
    </div>
  );
}

/* V4-C: Calculator — animated ROAS calculator */
function V4C() {
  const [step, setStep] = useState(0);
  const [roas, setRoas] = useState(1.0);

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setRoas(1.0); await w(800);
        setStep(1); await w(2000); // "your current ROAS"
        setStep(2); await w(1500); // why
        setStep(3); await w(1200); // fix 1
        setStep(4); await w(1200); // fix 2
        setStep(5); await w(1200); // fix 3
        setStep(6); // ROAS climbing
        for (let i = 10; i <= 42; i++) { if (!running) return; setRoas(i / 10); await w(50); }
        await w(600);
        setStep(7); await w(4000); // CTA
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  const roasColor = roas < 2 ? "#EF4444" : roas < 3 ? "#F59E0B" : "#10B981";

  return (
    <div style={{ width: "100%", height: "100%", background: "#fff", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 600, color: "#64748b" }}>ROAS Calculator</div>
      </div>

      <div style={{ flex: 1, padding: "0 20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {/* Big ROAS number */}
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 9, color: "#64748b", fontWeight: 600, marginBottom: 4 }}>Your ROAS</div>
          <div style={{ fontSize: 52, fontWeight: 900, color: roasColor, lineHeight: 1, transition: "color 0.3s ease" }}>
            {roas.toFixed(1)}x
          </div>
          <div style={{ fontSize: 10, color: roas >= 3 ? "#059669" : "#dc2626", fontWeight: 600, marginTop: 4 }}>
            {roas >= 3 ? "Excellent ROI 🎉" : roas >= 2 ? "Getting better..." : "Below profitable"}
          </div>
        </div>

        {/* Why it's low */}
        {step >= 2 && step < 6 && (
          <div style={{ textAlign: "center", marginBottom: 12, animation: "popIn 0.4s ease" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}>Why? Your feed drags your ROAS down.</div>
          </div>
        )}

        {/* Fixes */}
        {step >= 3 && step < 6 && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 4 }}>
            {[
              { s: 3, label: "Fix titles", impact: "+0.4x ROAS" },
              { s: 4, label: "Fix descriptions", impact: "+0.6x ROAS" },
              { s: 5, label: "Add highlights", impact: "+0.2x ROAS" },
            ].map((f, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "6px 12px", background: step >= f.s ? "#f0fdf4" : "#fafafa",
                borderRadius: 8, border: `1px solid ${step >= f.s ? "#bbf7d0" : "#f1f1f1"}`,
                opacity: step >= f.s ? 1 : 0, transition: "all 0.5s ease",
              }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: "#0f172a" }}>{f.label}</span>
                <span style={{ fontSize: 10, fontWeight: 800, color: "#10B981" }}>{f.impact}</span>
              </div>
            ))}
          </div>
        )}

        {step >= 7 && (
          <div style={{ textAlign: "center", marginTop: 8, animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 11, color: "#059669", fontWeight: 700 }}>= +38% revenue. Same budget.</div>
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #059669, #10B981)", color: "#fff", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, boxShadow: "0 4px 14px rgba(5,150,105,0.35)" }}>
          Boost My ROAS — Free Audit
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   V5 — ULTRA COURT
   "Enter your URL. Get your score. 30 seconds."
   ══════════════════════════════════════════════════ */

/* V5-A: Speed — minimal, fast, punchy */
function V5A() {
  const [step, setStep] = useState(0);
  const [typedUrl, setTypedUrl] = useState("");
  const [score, setScore] = useState(0);
  const URL_TEXT = "mystore.myshopify.com";

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setTypedUrl(""); setScore(0); await w(600);
        setStep(1); await w(1500); // hook
        setStep(2); // typing
        for (let i = 1; i <= URL_TEXT.length; i++) { if (!running) return; setTypedUrl(URL_TEXT.slice(0, i)); await w(35); }
        await w(400);
        setStep(3); await w(1000); // scanning
        setStep(4); // score
        for (let i = 0; i <= 32; i++) { if (!running) return; setScore(i); await w(40); }
        await w(800);
        setStep(5); await w(1000); // issues
        setStep(6); await w(1000);
        setStep(7); await w(1000);
        setStep(8); await w(4000); // CTA
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "#0f172a", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand light/>
        <div style={{ fontSize: 8, fontWeight: 600, color: "#64748b" }}>⚡ 30 seconds</div>
      </div>

      <div style={{ flex: 1, padding: "0 20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {step >= 1 && step < 3 && (
          <div style={{ textAlign: "center", marginBottom: 20, animation: "popIn 0.4s ease" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>Enter your URL.</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#818cf8", lineHeight: 1.2 }}>Get your score.</div>
          </div>
        )}

        {/* URL */}
        {step >= 2 && step < 4 && (
          <div style={{ width: "100%", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "10px 14px" }}>
              <span style={{ fontSize: 10, color: "#64748b", marginRight: 6 }}>🔗</span>
              <span style={{ fontSize: 12, color: "#e2e8f0", fontWeight: 500 }}>{typedUrl}</span>
              {typedUrl.length < URL_TEXT.length && <div style={{ width: 1, height: 14, background: "#818cf8", animation: "pulseMF 0.5s infinite", marginLeft: 1 }}/>}
            </div>
          </div>
        )}

        {step === 3 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, animation: "popIn 0.3s ease" }}>
            <div style={{ width: 12, height: 12, border: "2px solid #818cf8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
            <span style={{ fontSize: 10, color: "#818cf8", fontWeight: 600 }}>Scanning...</span>
          </div>
        )}

        {step >= 4 && (
          <div style={{ textAlign: "center" }}>
            <ScoreGauge score={score} size={110} strokeWidth={5}/>
            {step >= 5 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 12, width: "100%" }}>
                {[
                  { s: 5, text: "📝 Titles too short" },
                  { s: 6, text: "📄 Descriptions missing" },
                  { s: 7, text: "⭐ 0/5 highlights" },
                ].map((issue, i) => (
                  <div key={i} style={{
                    fontSize: 10, color: "#fca5a5", fontWeight: 600, textAlign: "center",
                    opacity: step >= issue.s ? 1 : 0, transition: "opacity 0.5s ease",
                  }}>{issue.text}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #818cf8, #a78bfa)", color: "#fff", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, boxShadow: "0 4px 14px rgba(129,140,248,0.4)" }}>
          ⚡ Get My Score — Free
        </div>
        <div style={{ fontSize: 8, color: "#64748b", marginTop: 6 }}>No account needed</div>
      </div>
    </div>
  );
}

/* V5-B: 3 Steps — super clean, 1-2-3 */
function V5B() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); await w(800);
        setStep(1); await w(2000); // step 1
        setStep(2); await w(2000); // step 2
        setStep(3); await w(2000); // step 3
        setStep(4); await w(2000); // "that's it"
        setStep(5); await w(4000); // CTA
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  const steps = [
    { num: "1", title: "Enter your URL", sub: "yourstore.myshopify.com", icon: "🔗" },
    { num: "2", title: "Get your score", sub: "AI scans your product feed", icon: "🔍" },
    { num: "3", title: "See what's wrong", sub: "Exact issues killing your visibility", icon: "📊" },
  ];

  return (
    <div style={{ width: "100%", height: "100%", background: "#fff", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand/>
        <div style={{ fontSize: 8, fontWeight: 600, color: "#94a3b8" }}>⚡ 30 seconds</div>
      </div>

      <div style={{ flex: 1, padding: "0 20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>Shopify + Google Shopping?</div>
        </div>

        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10 }}>
          {steps.map((s, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
              background: step >= (i + 1) ? "#f8faff" : "#fafafa",
              borderRadius: 12, border: `1px solid ${step >= (i + 1) ? "#dde5ff" : "#f1f1f1"}`,
              opacity: step >= (i + 1) ? 1 : 0.15,
              transform: step >= (i + 1) ? "translateX(0)" : "translateX(-20px)",
              transition: "all 0.6s ease",
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: step >= (i + 1) ? "linear-gradient(135deg, #4B6AE8, #818cf8)" : "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.5s ease" }}>
                <span style={{ fontSize: 16 }}>{s.icon}</span>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{s.title}</div>
                <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 1 }}>{s.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {step >= 4 && (
          <div style={{ marginTop: 16, textAlign: "center", animation: "popIn 0.5s ease" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#4B6AE8" }}>That's it. Free. No account.</div>
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #4B6AE8, #6B8BF5)", color: "#fff", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, boxShadow: "0 4px 14px rgba(75,106,232,0.35)" }}>
          ⚡ Start My Free Audit
        </div>
      </div>
    </div>
  );
}

/* V5-C: Countdown — 30 second timer */
function V5C() {
  const [step, setStep] = useState(0);
  const [timer, setTimer] = useState(30);
  const [score, setScore] = useState(0);

  useEffect(() => {
    let running = true;
    const loop = async () => {
      while (running) {
        const w = ms => new Promise(r => setTimeout(r, ms));
        setStep(0); setTimer(30); setScore(0); await w(600);
        setStep(1); await w(1500); // "30 seconds"
        setStep(2); // countdown
        for (let i = 30; i >= 0; i--) { if (!running) return; setTimer(i); await w(100); }
        setStep(3); // score
        for (let i = 0; i <= 32; i++) { if (!running) return; setScore(i); await w(40); }
        await w(600);
        setStep(4); await w(1000); // issues
        setStep(5); await w(1000);
        setStep(6); await w(1000);
        setStep(7); await w(4000); // CTA
      }
    };
    loop();
    return () => { running = false; };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #4B6AE8 0%, #3b5ce0 50%, #2d4ed8 100%)", fontFamily: "'Inter',sans-serif", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Brand light/>
        <div style={{ fontSize: 8, fontWeight: 600, color: "rgba(255,255,255,0.6)" }}>Free · No signup</div>
      </div>

      <div style={{ flex: 1, padding: "0 20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {step < 3 && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 64, fontWeight: 900, color: "#fff", lineHeight: 1 }}>{timer}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "rgba(255,255,255,0.8)", marginTop: 4 }}>seconds to audit your feed</div>
            {step >= 1 && (
              <div style={{ marginTop: 12, fontSize: 11, color: "rgba(255,255,255,0.6)" }}>Enter your Shopify URL.<br/>See exactly what's wrong.</div>
            )}
          </div>
        )}

        {step >= 3 && (
          <div style={{ textAlign: "center" }}>
            <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: 20, padding: 16, display: "inline-block" }}>
              <ScoreGauge score={score} size={100} strokeWidth={5}/>
            </div>
            {step >= 4 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 14 }}>
                {[
                  { s: 4, text: "📝 Titles: FAIL" },
                  { s: 5, text: "📄 Descriptions: FAIL" },
                  { s: 6, text: "⭐ Highlights: FAIL" },
                ].map((issue, i) => (
                  <div key={i} style={{
                    fontSize: 11, color: "#fca5a5", fontWeight: 600,
                    opacity: step >= issue.s ? 1 : 0, transition: "opacity 0.5s ease",
                  }}>{issue.text}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px 14px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "#fff", color: "#4B6AE8", fontSize: 12, fontWeight: 700, padding: "10px 24px", borderRadius: 10, boxShadow: "0 4px 14px rgba(0,0,0,0.2)" }}>
          ⚡ Audit My Feed Now
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   VIDEO DOWNLOAD BUTTON
   ══════════════════════════════════════════════════ */

const VIDEO_SIZES = {
  "1:1": { w: 540, h: 540, label: "1:1" },
  "16:9": { w: 720, h: 405, label: "16:9" },
  "9:16": { w: 405, h: 720, label: "9:16" },
};

const KEYFRAMES_CSS = `
  @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
  @keyframes pulseMF { 0%,100%{opacity:1} 50%{opacity:0.3} }
  @keyframes popIn { 0%{opacity:0;transform:scale(0.75)} 100%{opacity:1;transform:scale(1)} }
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
`;

function DownloadButton({ name, Component }) {
  const [recording, setRecording] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentFormat, setCurrentFormat] = useState("");

  const startRecording = useCallback(async (formatKey) => {
    const vf = VIDEO_SIZES[formatKey];
    setRecording(true); setProgress(0); setCurrentFormat(formatKey);
    const container = document.createElement("div");
    container.style.cssText = `position:fixed;top:-9999px;left:-9999px;width:${vf.w}px;height:${vf.h}px;overflow:hidden;z-index:-1;pointer-events:none;`;
    document.body.appendChild(container);
    const style = document.createElement("style");
    style.textContent = KEYFRAMES_CSS;
    container.appendChild(style);
    const wrapper = document.createElement("div");
    wrapper.style.cssText = `width:${vf.w}px;height:${vf.h}px;font-family:'Inter',sans-serif;`;
    container.appendChild(wrapper);
    const reactRoot = ReactDOM.createRoot(wrapper);
    reactRoot.render(<Component />);
    await new Promise(r => setTimeout(r, 2000));
    const canvas = document.createElement("canvas");
    canvas.width = vf.w; canvas.height = vf.h;
    const ctx = canvas.getContext("2d");
    const stream = canvas.captureStream(30);
    let mimeType = "video/webm;codecs=vp9";
    if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = "video/webm";
    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 5000000 });
    const chunks = [];
    recorder.ondataavailable = e => e.data.size > 0 && chunks.push(e.data);
    recorder.start(100);
    const duration = 17000;
    const start = Date.now();
    while (Date.now() - start < duration) {
      try {
        const c = await html2canvas(wrapper, { width: vf.w, height: vf.h, scale: 1, useCORS: true, logging: false });
        ctx.drawImage(c, 0, 0);
      } catch (e) {}
      setProgress(Math.min(100, Math.round(((Date.now() - start) / duration) * 100)));
      await new Promise(r => setTimeout(r, 30));
    }
    recorder.stop();
    await new Promise(r => { recorder.onstop = r; });
    const blob = new Blob(chunks, { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `MFP-V2-${name.replace(/[^a-zA-Z0-9]/g, "-")}-${formatKey.replace(":", "x")}.webm`;
    a.click();
    URL.revokeObjectURL(url);
    reactRoot.unmount();
    document.body.removeChild(container);
    setRecording(false); setProgress(0); setCurrentFormat("");
  }, [name, Component]);

  if (recording) {
    return (
      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 12, height: 12, border: "2px solid #4B6AE8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}/>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, color: "#4B6AE8", fontWeight: 600, marginBottom: 3 }}>Recording {currentFormat}... {progress}%</div>
          <div style={{ width: "100%", height: 3, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ width: `${progress}%`, height: "100%", background: "linear-gradient(90deg, #4B6AE8, #818cf8)", borderRadius: 2, transition: "width 0.3s ease" }}/>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
      {Object.entries(VIDEO_SIZES).map(([key, vf]) => (
        <button key={key} onClick={() => startRecording(key)} style={{
          background: "none", border: "1px solid #e5e7eb", borderRadius: 6,
          padding: "5px 10px", fontSize: 10, color: "#6b7280", cursor: "pointer",
          fontFamily: "'Inter',sans-serif", fontWeight: 600, display: "flex", alignItems: "center", gap: 4,
        }}>
          <span>🎬</span> {vf.label}
        </button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN EXPORT
   ══════════════════════════════════════════════════ */

const ANGLE_COLORS = {
  "V1": "#4B6AE8",
  "V2": "#EF4444",
  "V3": "#0284c7",
  "V4": "#10B981",
  "V5": "#7c3aed",
};

const FB_ADS_V2 = [
  { angle: "V1", angleName: "Curiosité", name: "Score Reveal", desc: "Gauge counting with suspense → What's your score?", Component: V1A },
  { angle: "V1", angleName: "Curiosité", name: "Question Stack", desc: "Questions stacking → score reveal in dark mode", Component: V1B },
  { angle: "V1", angleName: "Curiosité", name: "Scratch Card", desc: "Golden scratch card revealing bad score", Component: V1C },

  { angle: "V2", angleName: "Douleur", name: "Bleeding Sales", desc: "87% stat + 3 mistakes red alert", Component: V2A },
  { angle: "V2", angleName: "Douleur", name: "Pulse Monitor", desc: "Dark heartbeat flatline — feed is dying", Component: V2B },
  { angle: "V2", angleName: "Douleur", name: "Warning Tape", desc: "Construction zone — 3 fixable mistakes", Component: V2C },

  { angle: "V3", angleName: "Benchmark", name: "Bell Curve", desc: "800+ audited — side-by-side gauges", Component: V3A },
  { angle: "V3", angleName: "Benchmark", name: "Leaderboard", desc: "Ranked list — you're below average", Component: V3B },
  { angle: "V3", angleName: "Benchmark", name: "Distribution", desc: "Histogram — 63% score under 40", Component: V3C },

  { angle: "V4", angleName: "ROAS", name: "Revenue Counter", desc: "+67% impressions → +43% clicks → +38% revenue", Component: V4A },
  { angle: "V4", angleName: "ROAS", name: "Before/After", desc: "Dark dashboard split — same budget, more revenue", Component: V4B },
  { angle: "V4", angleName: "ROAS", name: "ROAS Calculator", desc: "1.0x → 4.2x ROAS climbing animation", Component: V4C },

  { angle: "V5", angleName: "Ultra Court", name: "Speed Dark", desc: "Enter URL → get score → minimal dark theme", Component: V5A },
  { angle: "V5", angleName: "Ultra Court", name: "3 Steps", desc: "Super clean 1-2-3 steps — that's it", Component: V5B },
  { angle: "V5", angleName: "Ultra Court", name: "Countdown", desc: "30-second timer on blue gradient", Component: V5C },
];

export default function FacebookAdsV2() {
  let lastAngle = "";

  return (
    <div>
      {FB_ADS_V2.map((ad, i) => {
        const showHeader = ad.angle !== lastAngle;
        lastAngle = ad.angle;
        const color = ANGLE_COLORS[ad.angle] || "#4B6AE8";

        return (
          <div key={i}>
            {showHeader && (
              <div style={{ marginTop: i > 0 ? 40 : 0, marginBottom: 16, paddingBottom: 8, borderBottom: `2px solid ${color}20` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color, textTransform: "uppercase", letterSpacing: 1.5 }}>{ad.angle}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>— {ad.angleName}</div>
                </div>
              </div>
            )}
            <div style={{ marginBottom: 24, display: "inline-block", verticalAlign: "top", marginRight: 24 }}>
              <div style={{
                width: 340, aspectRatio: "1/1",
                borderRadius: 14, overflow: "hidden",
                boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
                border: "1px solid rgba(0,0,0,0.06)",
              }}>
                <ad.Component />
              </div>
              <div style={{ marginTop: 10, display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color }}>{ad.angle}-{String((i % 3) + 1)}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{ad.name}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>{ad.desc}</div>
                </div>
              </div>
              <DownloadButton name={`${ad.angle}-${ad.name}`} Component={ad.Component}/>
            </div>
          </div>
        );
      })}
    </div>
  );
}
